/**
 * demoFaker.js — deterministic, locale-aware synthetic value generators.
 *
 * Every generator is a pure function of its ORIGINAL input value: the same real
 * name always maps to the same fake name, everywhere in the app (POS ↔
 * management ↔ service ↔ warehouse ↔ realtime), because the fake is seeded from
 * a hash of the original and memoised. That cross-module consistency is what
 * makes the demo look like a real, coherent system rather than random noise.
 *
 * `@faker-js/faker` provides the rich, locale-correct data (names, companies,
 * cities matching the operator's UI language). It is loaded through a guarded
 * `require` and, if it is missing or fails to initialise (e.g. an odd Hermes
 * edge), every generator transparently falls back to a small built-in
 * deterministic pool — so demo mode is always functional and never crashes the
 * bundle.
 *
 * @module app/demo/demoFaker
 */

import i18n from '../../i18n';

/** i18n language code → @faker-js/faker locale bundle name. */
const LANG_TO_FAKER = {hu: 'hu', en: 'en', de: 'de', cn: 'zh_CN', ro: 'ro', svn: 'sl'};

/** @type {*} cached faker module (undefined = not tried, null = unavailable). */
let fakerModule;
/** @type {Map<string, *>} one Faker instance per language. */
const fakerByLang = new Map();
/** @type {Map<string, string>} memoised `${lang}:${type}:${orig}` → fake value. */
const valueCache = new Map();

/* ───────────────────────── deterministic primitives ───────────────────────── */

/**
 * FNV-1a 32-bit hash — turns any original value into a stable integer seed.
 * @param {*} value input.
 * @returns {number} unsigned 32-bit seed.
 */
function hashSeed(value) {
    const s = String(value == null ? '' : value);
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }

    return h >>> 0;
}

/**
 * mulberry32 — a tiny deterministic PRNG for the built-in fallback pools.
 * @param {number} seed 32-bit seed.
 * @returns {() => number} generator returning floats in [0, 1).
 */
function mulberry32(seed) {
    let a = seed >>> 0;

    return function next() {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;

        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/**
 * Deterministically pick one element of a pool for a given seed.
 * @param {Array<*>} pool candidates.
 * @param {number} seed 32-bit seed.
 * @returns {*} the chosen element.
 */
function pickFrom(pool, seed) {
    return pool[seed % pool.length];
}

/* ───────────────────────── built-in fallback pools ───────────────────────── */

const FIRST_NAMES = ['Anna', 'Bence', 'Csaba', 'Dóra', 'Eszter', 'Gábor', 'Hanna', 'István',
    'Júlia', 'Károly', 'Laura', 'Márk', 'Nóra', 'Olivér', 'Petra', 'Réka', 'Sándor', 'Tamás',
    'Viktor', 'Zsófia', 'Ádám', 'Emma', 'Levente', 'Boróka'];
const LAST_NAMES = ['Kovács', 'Nagy', 'Tóth', 'Szabó', 'Horváth', 'Varga', 'Kiss', 'Molnár',
    'Németh', 'Farkas', 'Balogh', 'Papp', 'Takács', 'Juhász', 'Lakatos', 'Mészáros', 'Simon',
    'Fekete', 'Fehér', 'Szűcs'];
const COMPANY_CORES = ['Aurora', 'Borostyán', 'Céh', 'Delta', 'Enigma', 'Forrás', 'Gránit',
    'Horizont', 'Iris', 'Jáde', 'Kaptár', 'Lumen', 'Meridián', 'Nexus', 'Orion', 'Prizma',
    'Quantum', 'Rubin', 'Sirály', 'Trigon'];
const COMPANY_SUFFIX = ['Kft.', 'Bt.', 'Zrt.', 'Nyrt.'];
const STREET_CORES = ['Fő', 'Kossuth', 'Petőfi', 'Rákóczi', 'Ady', 'Bartók', 'Dózsa', 'Béke',
    'Arany', 'Jókai', 'Táncsics', 'Deák', 'Széchenyi', 'Zrínyi'];
const STREET_TYPES = ['utca', 'út', 'tér', 'körút', 'sétány'];
const CITIES = ['Budapest', 'Debrecen', 'Szeged', 'Miskolc', 'Pécs', 'Győr', 'Nyíregyháza',
    'Kecskemét', 'Székesfehérvár', 'Szombathely', 'Veszprém', 'Eger', 'Sopron', 'Kaposvár'];
const EMAIL_DOMAINS = ['example.com', 'demo.hu', 'mail.example', 'teszt.hu', 'sample.co'];

/* Venue (restaurant / store) name building blocks — kept on-brand Hungarian so a
 * faked store name still reads like a real hospitality venue in any demo. */
const VENUE_ADJ = ['Arany', 'Kék', 'Régi', 'Napfény', 'Zöld', 'Piros', 'Öreg', 'Kis',
    'Nagy', 'Fehér', 'Aranyhíd', 'Belvárosi', 'Panoráma', 'Duna', 'Vidám', 'Szép'];
const VENUE_NOUN = ['Kakas', 'Szarvas', 'Hordó', 'Malom', 'Kert', 'Sarok', 'Pince', 'Liget',
    'Fészek', 'Csárda', 'Halásztanya', 'Terasz', 'Udvar', 'Kastély', 'Bástya', 'Híd'];
const VENUE_TYPE = ['Étterem', 'Bisztró', 'Kávézó', 'Vendéglő', 'Söröző', 'Pizzéria',
    'Grill', 'Csárda', 'Kocsma', 'Cukrászda'];
/* Place / area (room, terrace, section) names inside a venue. */
const PLACE_NAMES = ['Terasz', 'Kerthelyiség', 'Belső terem', 'Emeleti terem', 'Bár pult',
    'VIP szoba', 'Panoráma terasz', 'Kávézó rész', 'Söröző', 'Rendezvényterem',
    'Utcafront', 'Galéria', 'Télikert', 'Különterem'];

/* Stable, food-themed stock photos (Unsplash CDN — long-lived permalinks) used to
 * fill the POS category/food grid tiles in demo mode so the menu looks stocked and
 * professional instead of one repeated "no image" placeholder. Bare photo ids; the
 * size/crop query is appended in {@link demoStockPhoto}. */
const FOOD_PHOTO_IDS = [
    '1546069901-ba9599a7e63c', '1565299624946-b28f40a0ae38', '1567620905732-2d1ec7ab7445',
    '1565958011703-44f9829ba187', '1504674900247-0877df9cc836', '1476224203421-9ac39bcb3327',
    '1540189549336-e6e99c3679fe', '1512621776951-a57141f2eefd', '1414235077428-338989a2e8c0',
    '1482049016688-2d3e1b311543', '1551782450-a2132b4ba21d', '1565895405227-31cffbe0cf86',
    '1544145945-f90425340c7e', '1558030006-450675393462', '1481931098730-318b6f776db0',
    '1467003909585-2f8a72700288', '1432139555190-58524dae6a55', '1490474418585-ba9bad8fd0ea',
    '1473093295043-cdd812d0e601', '1484723091739-30a097e8f929', '1455619452474-d2be8b1e70cd',
    '1517244683847-7456b63c5969', '1495474472287-4d71bcdd2085', '1414235077428-338989a2e8c0'];

/* ───────────────────────── faker plumbing ───────────────────────── */

/**
 * Guarded, one-time load of the faker module.
 * @returns {*} the module, or null when unavailable.
 */
function loadFakerModule() {
    if (fakerModule !== undefined) {
        return fakerModule;
    }
    try {
        // eslint-disable-next-line global-require
        fakerModule = require('@faker-js/faker');
    } catch (_) {
        fakerModule = null;
    }

    return fakerModule;
}

/**
 * The current app language, normalised to a supported key (defaults to `hu`).
 * @returns {string} language code.
 */
function currentLang() {
    const raw = (i18n && i18n.language ? String(i18n.language) : 'hu').toLowerCase();

    return LANG_TO_FAKER[raw] ? raw : 'hu';
}

/**
 * A Faker instance for the current language (memoised), or null if faker is
 * unavailable / failed to construct.
 * @returns {*} Faker instance or null.
 */
function getFaker() {
    const lang = currentLang();
    if (fakerByLang.has(lang)) {
        return fakerByLang.get(lang);
    }

    const mod = loadFakerModule();
    let instance = null;
    if (mod && typeof mod.Faker === 'function') {
        try {
            const primary = LANG_TO_FAKER[lang] || 'hu';
            const all = mod.allLocales || {};
            const chain = [all[primary], all.hu, all.en, mod.base].filter(Boolean);
            instance = new mod.Faker({locale: chain.length ? chain : [mod.en || all.en]});
        } catch (_) {
            instance = null;
        }
    }
    fakerByLang.set(lang, instance);

    return instance;
}

/**
 * Core generator: memoised, deterministic, faker-preferred with fallback.
 * @param {string} type generator kind (also part of the cache/seed key).
 * @param {*} orig the original value being replaced.
 * @param {(f: *) => (string|undefined)} viaFaker produce with a seeded Faker.
 * @param {(rand: () => number, seed: number) => string} viaFallback produce with the PRNG.
 * @returns {string} the deterministic fake value.
 */
function gen(type, orig, viaFaker, viaFallback) {
    const lang = currentLang();
    const key = `${lang}:${type}:${orig}`;
    const cached = valueCache.get(key);
    if (cached !== undefined) {
        return cached;
    }

    const seed = hashSeed(`${type}|${orig}`);
    let out;

    const f = getFaker();
    if (f) {
        try {
            f.seed(seed);
            out = viaFaker(f);
        } catch (_) {
            out = undefined;
        }
    }

    if (out == null || out === '') {
        out = viaFallback(mulberry32(seed), seed);
    }

    out = String(out);

    // Never let the fake equal the real value (a rare fallback-pool collision,
    // e.g. a real surname that lives in our pool) — that would leak the original.
    // The re-roll is seeded deterministically so consistency still holds.
    if (orig != null && orig !== '' && out.toLowerCase() === String(orig).toLowerCase()) {
        const seed2 = (seed + 0x9e3779b9) >>> 0;
        out = String(viaFallback(mulberry32(seed2), seed2));
    }

    valueCache.set(key, out);

    return out;
}

/* ───────────────────────── public generators ───────────────────────── */

/**
 * @param {*} orig original first name.
 * @returns {string} deterministic fake first name.
 */
export function fakeFirstName(orig) {
    return gen('firstName', orig, (f) => f.person.firstName(), (r, seed) => pickFrom(FIRST_NAMES, seed));
}

/**
 * @param {*} orig original last name.
 * @returns {string} deterministic fake last name.
 */
export function fakeLastName(orig) {
    return gen('lastName', orig, (f) => f.person.lastName(), (r, seed) => pickFrom(LAST_NAMES, seed));
}

/**
 * @param {*} orig original full name.
 * @returns {string} deterministic fake full name.
 */
export function fakeFullName(orig) {
    return gen('fullName', orig, (f) => f.person.fullName(), (r, seed) => {
        const last = pickFrom(LAST_NAMES, seed);
        const first = pickFrom(FIRST_NAMES, Math.imul(seed, 2654435761) >>> 0);

        // Hungarian order (family name first); good enough for a demo.
        return `${last} ${first}`;
    });
}

/**
 * @param {*} orig original company name.
 * @returns {string} deterministic fake company name.
 */
export function fakeCompany(orig) {
    return gen('company', orig, (f) => f.company.name(), (r, seed) => {
        const core = pickFrom(COMPANY_CORES, seed);
        const suffix = pickFrom(COMPANY_SUFFIX, Math.imul(seed, 40503) >>> 0);

        return `${core} ${suffix}`;
    });
}

/**
 * @param {*} orig original e-mail address.
 * @returns {string} deterministic fake e-mail.
 */
export function fakeEmail(orig) {
    return gen('email', orig, null, (r, seed) => {
        const first = pickFrom(FIRST_NAMES, seed).toLowerCase();
        const last = pickFrom(LAST_NAMES, Math.imul(seed, 2246822519) >>> 0).toLowerCase();
        const num = seed % 100;
        const domain = pickFrom(EMAIL_DOMAINS, Math.imul(seed, 40503) >>> 0);

        return `${deaccent(first)}.${deaccent(last)}${num}@${domain}`;
    });
}

/**
 * @param {*} orig original phone number.
 * @returns {string} deterministic fake HU-style phone number.
 */
export function fakePhone(orig) {
    return gen('phone', orig, null, (r, seed) => {
        const carriers = ['20', '30', '70'];
        const carrier = carriers[seed % carriers.length];
        const a = 100 + (seed % 900);
        const b = 1000 + ((seed >>> 3) % 9000);

        return `+36 ${carrier} ${a} ${b}`;
    });
}

/**
 * @param {*} orig original street.
 * @returns {string} deterministic fake street line.
 */
export function fakeStreet(orig) {
    return gen('street', orig, (f) => f.location.street(), (r, seed) => {
        const core = pickFrom(STREET_CORES, seed);
        const type = pickFrom(STREET_TYPES, Math.imul(seed, 40503) >>> 0);

        return `${core} ${type}`;
    });
}

/**
 * @param {*} orig original city.
 * @returns {string} deterministic fake city.
 */
export function fakeCity(orig) {
    return gen('city', orig, (f) => f.location.city(), (r, seed) => pickFrom(CITIES, seed));
}

/**
 * @param {*} orig original postal code.
 * @returns {string} deterministic fake 4-digit HU postcode.
 */
export function fakeZip(orig) {
    return gen('zip', orig, null, (r, seed) => String(1000 + (seed % 8999)));
}

/**
 * @param {*} orig original house number.
 * @returns {string} deterministic fake house number.
 */
export function fakeHouseNumber(orig) {
    return gen('houseNumber', orig, null, (r, seed) => String(1 + (seed % 120)));
}

/**
 * @param {*} orig original address.
 * @returns {string} deterministic fake full address line.
 */
export function fakeAddress(orig) {
    return gen('address', orig, null, (r, seed) => {
        const zip = String(1000 + (seed % 8999));
        const city = pickFrom(CITIES, seed);
        const street = pickFrom(STREET_CORES, Math.imul(seed, 2654435761) >>> 0);
        const type = pickFrom(STREET_TYPES, Math.imul(seed, 40503) >>> 0);
        const num = 1 + (seed % 120);

        return `${zip} ${city}, ${street} ${type} ${num}.`;
    });
}

/**
 * @param {*} orig original tax number.
 * @returns {string} deterministic fake HU-style tax number (########-#-##).
 */
export function fakeTaxNumber(orig) {
    return gen('taxNumber', orig, null, (r, seed) => {
        const base = String(10000000 + (seed % 89999999));
        const check = seed % 10;
        const county = 2 + (seed % 42);

        return `${base}-${check}-${String(county).padStart(2, '0')}`;
    });
}

/**
 * @param {*} orig original bank account / IBAN.
 * @returns {string} deterministic fake HU IBAN-like value.
 */
export function fakeIban(orig) {
    return gen('iban', orig, null, (r, seed) => {
        let digits = '';
        let s = seed;
        for (let i = 0; i < 24; i++) {
            s = Math.imul(s ^ (s >>> 13), 2654435761) >>> 0;
            digits += String(s % 10);
        }

        return `HU${String(10 + (seed % 89))}${digits}`.replace(/(.{4})/g, '$1 ').trim();
    });
}

/**
 * Strip Hungarian accents so generated e-mail local parts stay ASCII-safe.
 * @param {string} s input.
 * @returns {string} de-accented lowercase string.
 */
function deaccent(s) {
    return String(s)
        .replace(/[áàâä]/g, 'a').replace(/[éèêë]/g, 'e').replace(/[íìîï]/g, 'i')
        .replace(/[óòôöő]/g, 'o').replace(/[úùûüű]/g, 'u').replace(/[^a-z0-9.]/g, '');
}

/**
 * A deterministic fake restaurant / store / venue name. Unlike a personal or
 * company name, this reads like a hospitality venue ("Aranyhíd Bisztró"), so a
 * faked store name in the header / hero / scope label still looks real.
 * @param {*} orig original venue name.
 * @returns {string} deterministic fake venue name.
 */
export function fakeVenueName(orig) {
    return gen('venue', orig, null, (r, seed) => {
        const adj = pickFrom(VENUE_ADJ, seed);
        const noun = pickFrom(VENUE_NOUN, Math.imul(seed, 2654435761) >>> 0);
        const type = pickFrom(VENUE_TYPE, Math.imul(seed, 40503) >>> 0);

        // Two shapes: "Arany Kakas Étterem" or "Napfény Bisztró".
        return (seed & 1) ? `${adj} ${noun} ${type}` : `${adj} ${type}`;
    });
}

/**
 * A deterministic fake place / area name (a section, room or terrace within a
 * venue), e.g. "Panoráma terasz". Used for the selected-place scope label.
 * @param {*} orig original place name.
 * @returns {string} deterministic fake place name.
 */
export function fakePlaceName(orig) {
    return gen('place', orig, null, (r, seed) => pickFrom(PLACE_NAMES, seed));
}

/**
 * A deterministic food/drink stock photo URL for a POS grid tile. The same seed
 * (usually a food/category id) always maps to the same photo, so tiles don't
 * reshuffle between renders. Used ONLY in demo mode to fill tiles that have no
 * real image, so the menu grid looks stocked and professional.
 * @param {*} seed stable seed (e.g. the food/category `_id`).
 * @param {string} [kind='food'] `food` or `category` — varies the pick a little.
 * @returns {string} an Unsplash CDN image URL (400×300, crop-formatted).
 */
export function demoStockPhoto(seed, kind = 'food') {
    const id = pickFrom(FOOD_PHOTO_IDS, hashSeed(`${kind}|${seed}`));

    return `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=400&h=300&q=60`;
}

/* ───────────────────────── synthesis toolkit ───────────────────────── */

/**
 * A tiny deterministic RNG bundle keyed by a seed string — the primitive the
 * demo ENRICHER uses to fabricate coherent synthetic rows (orders, invoices,
 * bookings…) for empty screens. Unlike the memoised {@link gen} generators
 * above (keyed by an original value for cross-module CONSISTENCY), this is keyed
 * by an arbitrary seed (usually `resource|index`) so each fabricated row is
 * distinct yet stable across refetches.
 * @param {string} seedString stable seed (e.g. `order|7`).
 * @returns {{
 *   next: () => number,
 *   int: (min: number, max: number) => number,
 *   float: (min: number, max: number, dec?: number) => number,
 *   money: (min: number, max: number, step?: number) => number,
 *   bool: (p?: number) => boolean,
 *   chance: (p: number) => boolean,
 *   pick: <T>(arr: T[]) => T,
 *   weighted: (pairs: Array<[*, number]>) => *,
 *   hexId: () => string,
 *   dateWithin: (startMs: number, endMs: number) => Date,
 * }} the RNG bundle.
 */
export function createRng(seedString) {
    const rand = mulberry32(hashSeed(String(seedString)));

    const int = (min, max) => min + Math.floor(rand() * (max - min + 1));
    const pick = (arr) => (Array.isArray(arr) && arr.length ? arr[Math.floor(rand() * arr.length)] : undefined);

    return {
        next: rand,
        int,
        float: (min, max, dec = 2) => {
            const v = min + rand() * (max - min);
            const p = 10 ** dec;

            return Math.round(v * p) / p;
        },
        money: (min, max, step = 10) => Math.max(0, Math.round((min + rand() * (max - min)) / step) * step),
        bool: (p = 0.5) => rand() < p,
        chance: (p) => rand() < p,
        pick,
        weighted: (pairs) => {
            const list = Array.isArray(pairs) ? pairs : [];
            const total = list.reduce((s, [, w]) => s + (Number(w) || 0), 0);
            if (total <= 0) {
                return list.length ? list[0][0] : undefined;
            }
            let t = rand() * total;
            for (let i = 0; i < list.length; i++) {
                t -= Number(list[i][1]) || 0;
                if (t <= 0) {
                    return list[i][0];
                }
            }

            return list[list.length - 1][0];
        },
        hexId: () => {
            let s = '';
            let h = hashSeed(`${seedString}|hex|${rand()}`);
            for (let i = 0; i < 24; i++) {
                h = Math.imul(h ^ (h >>> 13), 2654435761) >>> 0;
                s += (h & 0xf).toString(16);
            }

            return s;
        },
        dateWithin: (startMs, endMs) => {
            const lo = Math.min(startMs, endMs);
            const hi = Math.max(startMs, endMs);

            return new Date(lo + rand() * Math.max(1, hi - lo));
        },
    };
}

/**
 * A distinct-but-deterministic fake person for a synthetic row (fabrication, not
 * redaction). Seeded by index so every fabricated customer/guest is a different
 * believable person, stable across refetches.
 * @param {string|number} seed row seed (usually the index).
 * @returns {{first: string, last: string, full: string, email: string, phone: string}}
 */
export function fakePerson(seed) {
    const first = fakeFirstName(`demo-person-first-${seed}`);
    const last = fakeLastName(`demo-person-last-${seed}`);

    return {
        first,
        last,
        full: `${last} ${first}`,
        email: fakeEmail(`demo-person-${seed}`),
        phone: fakePhone(`demo-person-${seed}`),
    };
}

/**
 * Drop every memoised fake value (called on toggle / when the app wants a clean
 * session). Outputs remain deterministic afterwards.
 * @returns {void}
 */
export function clearFakeCache() {
    valueCache.clear();
}
